// src/services/commentService.ts
import { useCommentStore, type Comment } from '@/stores/commentStore'
import { useTimelineActivityStore } from '@/stores/timelineActivityStore'

/**
 * Comment Service - Provides business logic for comment-related operations
 */
export function useCommentService() {
  const commentStore = useCommentStore()
  const timelineStore = useTimelineActivityStore()

  /**
   * Add a comment to the timeline activity store
   */
  async function addCommentToTimeline(comment: Comment): Promise<void> {
    await timelineStore.add({
      type: 'comment',
      title: 'New comment',
      description: comment.content,
      createdAt: comment.createdAt,
      personId: comment.personId,
      organizationId: comment.organizationId,
      entityId: comment.id,
      entityType: 'comments',
      userId: comment.createdBy,
    })
  }

  return {
    /**
     * Add a new comment and record it in the timeline
     */
    async addComment(comment: Partial<Comment>): Promise<Comment> {
      const savedComment = await commentStore.add(comment)

      // Add to timeline
      await addCommentToTimeline(savedComment)

      return savedComment
    },

    /**
     * Get comments for a specific person
     */
    async getCommentsForPerson(personId: string): Promise<Comment[]> {
      await commentStore.load()
      return commentStore.findBy((comment: Comment) => comment.personId === personId)
    },

    /**
     * Get comments for a specific organization
     */
    async getCommentsForOrganization(organizationId: string): Promise<Comment[]> {
      await commentStore.load()
      return commentStore.findBy((comment: Comment) => comment.organizationId === organizationId)
    },

    /**
     * Get comments for a specific task
     */
    async getCommentsForTask(taskId: string): Promise<Comment[]> {
      await commentStore.load()
      return commentStore.findBy((comment: Comment) => comment.taskId === taskId)
    },
  }
}
